<script lang="ts" setup>
import { computed, onMounted, Ref, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Chip from 'primevue/chip';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import { useArchiveStore, useAuthStore, useClinicianStore } from '@/store';
import { ClinicianTableInterface } from '@/types';
import { can, isValidForRoleByTenant } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const archiveStore = useArchiveStore();
const authStore = useAuthStore();
const clinicianStore = useClinicianStore();
const clinicians: Ref<ClinicianTableInterface[]> = ref([]);
const clinicianCount: Ref<number> = computed(() => (clinicians.value ? clinicians.value.length : 0));
const clinicianModalOpen = ref(false);
const archivedVisible = ref(false);
const clinicianItems: Ref<{ name: string; value: number }[]> = ref([]);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_CLINICIANS_INDEX, PERMISSIONS.ORGANIZATION_CLINICIANS_INDEX_SELF]))
    getClinicians(false);
});

const getClinicians: (showArchived: boolean) => Promise<void> = async (showArchived: boolean) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  const clinicianData = await clinicianStore.getCliniciansForTable(filterArchived).catch((e) => {
    clinicians.value = [];
    throw e;
  });
  const filteredClinicians = clinicianData.filter(
    (clinician) => clinician.organizations && clinician.organizations[0].id === clinicianStore.orgId,
  );
  const parentOrgClinicians = clinicianData.filter(
    (clinician) => clinician.organizations && clinician.organizations[0].id != clinicianStore.orgId,
  );
  clinicians.value = [
    ...filteredClinicians.sort((a, b) => a.name.localeCompare(b.name)),
    ...parentOrgClinicians.sort((a, b) => a.name.localeCompare(b.name)),
  ];
};

const viewActions = ref(can([PERMISSIONS.ORGANIZATION_DETACH_CLINICIANS, PERMISSIONS.PEOPLE_UNARCHIVE]));

const createMenuModel = (item: ClinicianTableInterface) => {
  const menuItems = [];
  if (!authStore.currentInstanceIsMasterTenant && can([PERMISSIONS.ORGANIZATION_DETACH_CLINICIANS])) {
    menuItems.push({
      label: 'Remove Service Provider',
      command: () => {
        detachClinicians([item.id], item.name);
      },
    });
  }
  if (item.archived && can([PERMISSIONS.PEOPLE_UNARCHIVE])) {
    menuItems.push({
      label: 'Unarchive',
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachClinicians: (ids: number[]) => Promise<void> = async (ids) => {
  const response = await clinicianStore.attachClinicians({ clinicians: ids });
  if (response.status === 200) {
    await getClinicians(false);
    clinicianStore.doToast('Service Provider(s) has been added', 'Added');
  } else {
    clinicianStore.doToast('Service Provider(s) has not been added', 'Not Added', true);
  }
};

const detachClinicians: (ids: number[], clinicianName: string) => Promise<void> = async (ids, clinicianName) => {
  const response = await clinicianStore.detachClinicians({ clinicians: ids });
  if (response.status === 200) {
    clinicianStore.doToast(`${clinicianName} has been removed`, 'Removed');
    await getClinicians(false);
  } else {
    clinicianStore.doToast(`${clinicianName} hasn't been removed`, 'Not Removed', true);
  }
};

const openAddClinicians = async () => {
  clinicianModalOpen.value = true;
  clinicianItems.value = await clinicianStore.getAddCliniciansModalData();
};

const onCancelAdd = () => (clinicianModalOpen.value = false);

const onSaveAdd = (clinicians: { name: string; value: number }[]) => {
  let clinicianIds = clinicians.map((clinician) => {
    return clinician.value;
  });
  attachClinicians(clinicianIds);
  clinicianModalOpen.value = false;
};

const showArchived = () => {
  archivedVisible.value = true;
  clinicianStore.showArchived = true;
  getClinicians(true);
};
const hideArchived = () => {
  archivedVisible.value = false;
  clinicianStore.showArchived = false;
  getClinicians(false);
};

const setRowClass = (rowData: ClinicianTableInterface) => {
  let style =
    rowData.organizations && rowData.organizations[0].id != clinicianStore.orgId
      ? 'inherited-service-provider-row'
      : 'service-provider-row';

  if (rowData.archived) style += ' grey-row';

  return style;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Service Providers</h2>
      <Chip>{{ clinicianCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATIONS_CLINICIAN_SHOW_ARCHIVE])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div
        v-if="!authStore.currentInstanceIsMasterTenant && can([PERMISSIONS.ORGANIZATION_ATTACH_CLINICIANS])"
        class="ml-auto"
      >
        <AppButton
          id="add-button"
          type="text"
          size="small"
          text="Add Service Providers"
          icon-left="add"
          @click="openAddClinicians"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="clinicians"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="clinicianStore.loading"
        data-key="name"
        responsive-layout="scroll"
        :row-class="setRowClass"
        scroll-height="440px"
        state-key="session-store-clinicians-table"
        state-storage="session"
        :class="{ 'table-loading': clinicianStore.loading }"
      >
        <template v-if="clinicianCount">
          <Column
            body-style="max-width: 20rem"
            field="name"
            header="Service Provider Name"
            header-style="max-width: 20rem;"
            sortable
          >
            <template #body="{ data }">
              <Icon
                v-if="data.archived"
                v-tooltip.top="{
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                }"
                name="info"
                class="mr-1 text-color-gray-dark text-base cursor-pointer"
              />
              <RouterLink
                v-if="can([PERMISSIONS.PEOPLE_SHOW]) && isValidForRoleByTenant(data.people_type)"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.name"
              />
              <span v-else class="font-semibold" v-text="data.name" />
            </template>
          </Column>
          <Column field="disciplines" header="Disciplines">
            <template #body="{ data }">
              <span v-text="data.disciplines.join(', ')" />
            </template>
          </Column>
          <Column field="organizations" header="Organization">
            <template #body="{ data }">
              <RouterLink
                v-if="data.organizations[0].id != clinicianStore.orgId"
                class="text-color-secondary underline"
                :to="`/organizations/${data.organizations[0].id}`"
                v-text="data.organizations[0].name"
              />
              <span v-else v-text="data.organizations[0].name" />
            </template>
          </Column>
          <Column
            v-if="viewActions"
            body-style="justify-content: center; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="data.organizations[0].id == clinicianStore.orgId && createMenuModel(data).length"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
              <span v-else>-</span>
            </template>
          </Column>
        </template>
        <template #empty>Service Provider not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="clinicianModalOpen && can([PERMISSIONS.ORGANIZATION_ATTACH_CLINICIANS])"
        title="Add service providers"
        :items="clinicianItems"
        :searchable="true"
        button-text="Add service providers"
        placeholder="Search service providers"
        person-type="clinician"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
.p-inputswitch-slider {
  background: $grayLight;
}
.archive_label {
  font-weight: 600;
  display: flex;
  align-items: center;
  color: $grayDark;
}

:deep(.inherited-service-provider-row) {
  td {
    background: var(--surface-50);
  }
}
</style>
